import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import "moment/locale/nl";
import Skeleton from "@mui/material/Skeleton";
import FlightLandRoundedIcon from "@mui/icons-material/FlightLandRounded";
import FlightTakeoffRoundedIcon from "@mui/icons-material/FlightTakeoffRounded";
import FlightRoundedIcon from "@mui/icons-material/FlightRounded";

import { ReactComponent as NetherLands } from "assets/images/nl.svg";
import { ReactComponent as Suriname } from "assets/images/sr.svg";
import { ReactComponent as Curacao } from "assets/images/cw.svg";

import useApi from "hooks/useApi";
import flightSchedulesApi from "api/FlightSchedules";
import { useTranslation } from "i18n";

import "./style.css";

const AIRPORTS = [
  { id: "Suriname", labelKey: "schedules.suriname", Flag: Suriname },
  { id: "Curacao", labelKey: "schedules.curacao", Flag: Curacao },
  { id: "Netherlands", labelKey: "schedules.netherlands", Flag: NetherLands },
];

const formatDuration = (dep, arr) => {
  const minutes = moment(arr).diff(moment(dep), "minutes");
  if (!Number.isFinite(minutes) || minutes <= 0) return "";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
};

const statusTone = (status) => {
  const value = String(status || "").toLowerCase();
  if (["delayed", "cancelled", "canceled", "diverted"].includes(value)) {
    return value === "canceled" ? "cancelled" : value;
  }
  if (["landed", "arrived"].includes(value)) return "landed";
  if (["active", "en-route", "enroute"].includes(value)) return "active";
  return "scheduled";
};

function FlightSchedules() {
  const { t, locale } = useTranslation();
  const getFlightSchedules = useApi(flightSchedulesApi);
  const [selected, setSelected] = useState({
    currentAirport: "Suriname",
    flightType: "Arrival",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    getFlightSchedules.request();
  }, []);

  const scheduleKey =
    selected.currentAirport.toLowerCase() + selected.flightType;
  const flights =
    getFlightSchedules?.data?.flightSchedules?.[scheduleKey] || [];
  const lastUpdated =
    getFlightSchedules?.data?.flightSchedules?.lastUpdatedCalculated;
  const typeLabel =
    selected.flightType === "Arrival"
      ? t("schedules.arrivals")
      : t("schedules.departures");

  const countLabel = useMemo(() => {
    if (flights.length === 1) return t("schedules.flightCountOne");
    return t("schedules.flightCount", { count: flights.length });
  }, [flights.length, t]);

  return (
    <div className="schedules-page">
      <section className="schedules-hero">
        <div className="schedules-hero__glow" aria-hidden="true" />
        <div className="container schedules-hero__content">
          <p className="schedules-hero__eyebrow">{t("schedules.heroEyebrow")}</p>
          <h1 className="schedules-hero__title">{t("schedules.heroTitle")}</h1>
          <p className="schedules-hero__subtitle">
            {t("schedules.heroSubtitle")}
          </p>
        </div>
      </section>

      <div className="container schedules-shell">
        <div className="schedules-board">
          <div className="schedules-airports" role="tablist">
            {AIRPORTS.map(({ id, labelKey, Flag }) => {
              const active = selected.currentAirport === id;
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`schedules-airport${active ? " is-active" : ""}`}
                  onClick={() =>
                    setSelected((prev) => ({ ...prev, currentAirport: id }))
                  }
                >
                  <Flag />
                  <span>{t(labelKey)}</span>
                </button>
              );
            })}
          </div>

          <div className="schedules-toolbar">
            <div className="schedules-toggle" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={selected.flightType === "Arrival"}
                className={
                  selected.flightType === "Arrival" ? "is-active" : ""
                }
                onClick={() =>
                  setSelected((prev) => ({ ...prev, flightType: "Arrival" }))
                }
              >
                <FlightLandRoundedIcon />
                {t("schedules.arrivals")}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={selected.flightType === "Departure"}
                className={
                  selected.flightType === "Departure" ? "is-active" : ""
                }
                onClick={() =>
                  setSelected((prev) => ({ ...prev, flightType: "Departure" }))
                }
              >
                <FlightTakeoffRoundedIcon />
                {t("schedules.departures")}
              </button>
            </div>

            <div className="schedules-meta">
              {!getFlightSchedules.loading && !getFlightSchedules.error && (
                <span className="schedules-count">{countLabel}</span>
              )}
              <div
                className="schedules-updated"
                title={t("schedules.lastUpdatedHint")}
              >
                <span className="schedules-updated__dot" />
                <span>
                  {t("schedules.lastUpdated")}
                  {": "}
                  {getFlightSchedules.loading ? (
                    <Skeleton
                      variant="text"
                      width={90}
                      sx={{ display: "inline-block" }}
                    />
                  ) : (
                    <strong>{lastUpdated || "—"}</strong>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="schedules-list">
          {getFlightSchedules.loading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <div className="schedules-card is-skeleton" key={index}>
                <Skeleton variant="rounded" height={18} width="40%" />
                <Skeleton variant="rounded" height={44} />
              </div>
            ))
          ) : getFlightSchedules.error ? (
            <div className="schedules-empty">
              <h2>{t("schedules.errorTitle")}</h2>
              <p>{t("schedules.errorMessage")}</p>
            </div>
          ) : flights.length === 0 ? (
            <div className="schedules-empty">
              <h2>{t("schedules.emptyTitle", { type: typeLabel.toLowerCase() })}</h2>
              <p>{t("schedules.emptyMessage")}</p>
            </div>
          ) : (
            flights.map((item, index) => {
              const duration = formatDuration(item?.depTime, item?.arrTime);
              const tone = statusTone(item?.status);
              const logo =
                item?.airlineLogos?.squareImage || item?.airlineLogos?.tailImage;
              const flightNumber =
                item?.flightIata || item?.airlineIata || "—";
              return (
                <article
                  className="schedules-card"
                  key={`${item?.airlineIata}-${item?.depTime}-${item?.arrTime}-${index}`}
                >
                  <header className="schedules-card__top">
                    <div className="schedules-airline">
                      {logo ? (
                        <img src={logo} alt={item?.airlineIata || ""} />
                      ) : (
                        <span className="schedules-airline__fallback">
                          <FlightRoundedIcon />
                        </span>
                      )}
                      <div>
                        <strong>{flightNumber}</strong>
                        <span>{item?.airlineIata}</span>
                      </div>
                    </div>
                    {item?.status && (
                      <span className={`schedules-status is-${tone}`}>
                        {item.status}
                      </span>
                    )}
                  </header>

                  <div className="schedules-route">
                    <div className="schedules-leg">
                      <span className="schedules-leg__label">
                        {t("schedules.from")}
                      </span>
                      <time>
                        {moment(item?.depTime).locale(locale).format("HH:mm")}
                      </time>
                      <strong>
                        {item?.departureIata || item?.departureAirport}
                      </strong>
                      <span>
                        {item?.departureCity}
                        {item?.depTime
                          ? ` · ${moment(item.depTime)
                              .locale(locale)
                              .format("ddd D MMM")}`
                          : ""}
                      </span>
                    </div>

                    <div className="schedules-path" aria-hidden="true">
                      <i />
                      <FlightRoundedIcon />
                      <i />
                      {duration && <small>{duration}</small>}
                    </div>

                    <div className="schedules-leg is-arrival">
                      <span className="schedules-leg__label">
                        {t("schedules.to")}
                      </span>
                      <time>
                        {moment(item?.arrTime).locale(locale).format("HH:mm")}
                      </time>
                      <strong>
                        {item?.arrivalIata || item?.arrivalAirport}
                      </strong>
                      <span>
                        {item?.arrivalCity}
                        {item?.arrTime
                          ? ` · ${moment(item.arrTime)
                              .locale(locale)
                              .format("ddd D MMM")}`
                          : ""}
                      </span>
                    </div>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default FlightSchedules;
